import type { EditableTokens, ResolvedAudienceSkinV1 } from '@/domain/release/types'

export interface ResolvedTheme {
  skinId: string
  elevationPreset: EditableTokens['elevation.preset']
  cssVariables: Record<string, string>
  canvas: {
    page: string
    surface: string
    elevated: string
    foreground: string
    secondary: string
    accent: string
    border: string
    routeEntry: string
    routeExit: string
    qrForeground: string
    qrBackground: string
    scrim: string
    inverse: string
  }
  broadcast: { surface: string; urgent: string; warning: string }
  annotation: { medical: string; accessible: string; service: string }
  vr: { toolbar: string; toolbarText: string }
  assets: { logo: string; hero: string; pageBackground: string }
}

const tokenVariables: Partial<Record<keyof EditableTokens, string[]>> = {
  'brand.primary': ['--vf-primary-bg', '--vf-route-entry', '--vf-save-accent'],
  'brand.onPrimary': ['--vf-primary-text', '--vf-language-active-text'],
  'brand.secondary': ['--vf-text-cyan', '--vf-icon-color'],
  'surface.page': ['--vf-page-bg', '--vf-body-bg', '--vf-shell-bg'],
  'surface.card': ['--vf-card-bg', '--vf-service-card-bg'],
  'surface.elevated': ['--vf-secondary-bg', '--vf-control-bg', '--vf-broadcast-sheet-bg'],
  'surface.scrim': ['--vf-broadcast-overlay', '--vf-image-preview-bg'],
  'text.primary': ['--vf-text-primary', '--vf-page-title', '--vf-card-title'],
  'text.secondary': ['--vf-text-secondary', '--vf-text-muted', '--vf-language-inactive-text'],
  'text.inverse': ['--vf-broadcast-text'],
  'border.subtle': ['--vf-card-border', '--vf-shell-border', '--vf-info-border'],
  'border.strong': ['--vf-boundary', '--vf-primary-border'],
  'focus.ring': ['--vf-focus-ring'],
}

const sharedUiRadii = ['12px', '18px', '20px'] as const
const radiusPresets = { compact: sharedUiRadii, balanced: sharedUiRadii, soft: sharedUiRadii } as const
const elevationPresets = {
  flat: ['none', 'none'],
  soft: ['0 8px 24px rgba(19,34,56,.08)', '0 16px 40px rgba(19,34,56,.12)'],
  layered: ['0 8px 22px rgba(2,8,20,.18)', '0 18px 42px rgba(2,8,20,.30)'],
} as const

let activeTheme: ResolvedTheme | null = null
const appliedVariables = new WeakMap<HTMLElement, Set<string>>()

export function createResolvedTheme(skin: ResolvedAudienceSkinV1): ResolvedTheme {
  if (skin.schemaVersion !== 'venue-audience-skin/v1' || skin.themeResolverVersion !== 'vf-theme-resolver/v1') throw new Error('不支持的皮肤解析器版本')
  const cssVariables: Record<string, string> = {}
  for (const [key, variables] of Object.entries(tokenVariables)) {
    const value = skin.tokens[key as keyof EditableTokens]
    for (const variable of variables || []) cssVariables[variable] = value
  }
  const radii = radiusPresets[skin.tokens['shape.radiusPreset'] as keyof typeof radiusPresets]
  const shadows = elevationPresets[skin.tokens['elevation.preset'] as keyof typeof elevationPresets]
  if (!radii || !shadows) throw new Error('皮肤使用了不支持的圆角或阴影预设')
  Object.assign(cssVariables, {
    '--vf-shell-overlay': 'none', '--vf-shell-overlay-opacity': '0',
    '--vf-pill-radius': '999px', '--vf-card-radius': radii[1], '--vf-hero-radius': radii[2],
    '--vf-control-radius': '16px', '--vf-action-radius': radii[2], '--vf-menu-card-radius': radii[1], '--vf-small-control-radius': radii[0],
    '--vf-card-icon-radius': '16px', '--vf-home-card-icon-radius': '16px', '--vf-menu-title-size': '17px', '--vf-menu-text-size': '14px',
    '--vf-card-shadow': shadows[0], '--vf-shell-shadow': shadows[1],
    '--vf-page-layer': `linear-gradient(180deg, ${skin.tokens['surface.page']}, ${skin.tokens['surface.page']})`,
    '--vf-topbar-bg': skin.tokens['surface.page'], '--vf-topbar-opaque-bg': skin.tokens['surface.page'], '--vf-topbar-title': skin.tokens['text.primary'],
    '--vf-topbar-icon': skin.tokens['text.primary'], '--vf-topbar-action': skin.tokens['text.primary'],
    '--vf-topbar-border': skin.tokens['border.subtle'], '--vf-topbar-shadow': shadows[0],
    '--vf-control-bg': skin.tokens['surface.elevated'], '--vf-control-border': skin.tokens['border.subtle'], '--vf-control-shadow': shadows[0],
    '--vf-language-bg': skin.tokens['surface.card'], '--vf-language-border': skin.tokens['border.subtle'], '--vf-language-shadow': shadows[0],
    '--vf-language-thumb-bg': skin.tokens['brand.primary'], '--vf-language-thumb-shadow': shadows[0],
    '--vf-card-bg': skin.tokens['surface.card'], '--vf-choice-card-bg': skin.tokens['surface.card'], '--vf-vr-card-bg': `linear-gradient(145deg, ${skin.tokens['brand.primary']}, ${skin.tokens['surface.elevated']})`,
    '--vf-card-highlight': 'none', '--vf-vr-card-border': skin.tokens['border.strong'],
    '--vf-icon-bg': skin.tokens['surface.elevated'], '--vf-icon-border': skin.tokens['border.strong'], '--vf-icon-shadow': shadows[0],
    '--vf-service-card-bg': skin.tokens['surface.card'], '--vf-primary-bg': skin.tokens['brand.primary'], '--vf-primary-border': skin.tokens['border.strong'], '--vf-primary-shadow': shadows[0],
    '--vf-secondary-bg': skin.tokens['surface.elevated'], '--vf-secondary-border': skin.tokens['border.subtle'],
    '--vf-text-action': skin.tokens['text.primary'], '--vf-text-action-bg': skin.tokens['surface.elevated'], '--vf-text-action-border': skin.tokens['border.subtle'], '--vf-text-action-shadow': shadows[0],
    '--vf-hero-bg': skin.tokens['surface.card'], '--vf-hero-border': skin.tokens['border.subtle'], '--vf-hero-shadow': shadows[0],
    '--vf-media-frame-bg': skin.tokens['surface.card'], '--vf-media-frame-border': skin.tokens['border.subtle'], '--vf-media-frame-shadow': shadows[0],
    '--vf-lost-border': skin.tokens['border.strong'], '--vf-info-shadow': shadows[0], '--vf-recommend-bg': skin.tokens['surface.elevated'],
    '--vf-entry-notice-bg': skin.tokens['surface.card'], '--vf-entry-notice-text': skin.tokens['text.primary'], '--vf-entry-notice-heading': skin.tokens['text.primary'],
    '--vf-entry-notice-link': skin.tokens['brand.secondary'], '--vf-entry-notice-quote-bg': skin.tokens['surface.elevated'],
    '--vf-entry-notice-quote-border': skin.tokens['border.strong'], '--vf-entry-notice-table-border': skin.tokens['border.subtle'],
    '--vf-map-core-fill': skin.tokens['surface.elevated'], '--vf-map-core-stroke': skin.tokens['border.strong'], '--vf-map-node-venue': skin.lockedTokens['route.entry'], '--vf-map-node-fill': skin.tokens['surface.card'],
    '--vf-status-error': skin.lockedTokens['status.urgent'],
    '--vf-broadcast-sheet-bg': skin.tokens['surface.elevated'], '--vf-broadcast-sheet-border': skin.tokens['border.subtle'], '--vf-broadcast-sheet-shadow': shadows[1],
    '--vf-broadcast-text': skin.tokens['text.primary'], '--vf-broadcast-accent': skin.tokens['brand.secondary'],
    '--vf-broadcast-close-bg': skin.tokens['surface.card'], '--vf-broadcast-close-border': skin.tokens['border.subtle'],
    '--vf-broadcast-muted': skin.tokens['text.secondary'], '--vf-broadcast-empty': skin.tokens['text.secondary'], '--vf-broadcast-item-bg': skin.tokens['surface.card'],
    '--vf-broadcast-time': skin.tokens['text.secondary'], '--vf-broadcast-body': skin.tokens['text.primary'],
    '--vf-broadcast-detail-muted': skin.tokens['text.secondary'], '--vf-broadcast-detail-trigger-text': skin.tokens['brand.secondary'],
    '--vf-broadcast-detail-trigger-bg': skin.tokens['surface.card'], '--vf-broadcast-detail-trigger-border': skin.tokens['border.subtle'],
    '--vf-broadcast-detail-overlay': skin.tokens['surface.scrim'], '--vf-broadcast-detail-border': skin.tokens['border.subtle'],
    '--vf-broadcast-gallery-bg': skin.tokens['surface.card'], '--vf-broadcast-detail-action-bg': skin.tokens['surface.card'], '--vf-broadcast-detail-action-border': skin.tokens['border.subtle'],
    '--vf-broadcast-legend-bg': skin.tokens['surface.card'], '--vf-broadcast-legend-border': skin.tokens['border.subtle'],
    '--vf-broadcast-ticker-bg': skin.tokens['surface.card'], '--vf-broadcast-ticker-shadow': shadows[0], '--vf-broadcast-ticker-warning-shadow': shadows[0],
    '--vf-emergency-overlay': skin.tokens['surface.scrim'], '--vf-emergency-card-bg': skin.tokens['surface.elevated'], '--vf-emergency-card-border': skin.lockedTokens['status.urgent'], '--vf-emergency-card-shadow': shadows[1],
    '--vf-emergency-gallery-bg': skin.tokens['surface.card'], '--vf-emergency-kicker': skin.lockedTokens['status.urgent'], '--vf-emergency-time': skin.tokens['text.secondary'], '--vf-emergency-body': skin.tokens['text.primary'],
    '--vf-annotation-region-bg': skin.tokens['surface.elevated'], '--vf-annotation-region-border': skin.lockedTokens['annotation.service'], '--vf-annotation-region-shadow': shadows[0],
    '--vf-annotation-overlay': skin.tokens['surface.scrim'], '--vf-annotation-text': skin.tokens['text.primary'], '--vf-annotation-panel-bg': skin.tokens['surface.card'],
    '--vf-annotation-panel-border': skin.tokens['border.subtle'], '--vf-annotation-panel-shadow': shadows[1], '--vf-annotation-head-bg': skin.tokens['surface.elevated'],
    '--vf-annotation-copy-bg': skin.tokens['surface.elevated'], '--vf-annotation-image-bg': skin.tokens['surface.card'], '--vf-annotation-caption-bg': skin.tokens['surface.elevated'],
    '--vf-summary-card-bg': skin.tokens['surface.card'], '--vf-summary-card-overlay': 'none',
    '--vf-summary-title': skin.tokens['text.primary'], '--vf-summary-inverse': skin.tokens['text.inverse'], '--vf-summary-muted': skin.tokens['text.secondary'],
    '--vf-summary-kicker': skin.tokens['brand.secondary'], '--vf-summary-snapshot-label': skin.tokens['text.secondary'],
    '--vf-summary-map-border': skin.tokens['border.subtle'], '--vf-summary-map-shadow': shadows[0],
    '--vf-summary-chip-bg': skin.lockedTokens['route.entry'], '--vf-summary-chip-border': skin.tokens['border.strong'], '--vf-summary-chip-shadow': shadows[0],
    '--vf-summary-step-bg': skin.lockedTokens['route.entry'], '--vf-summary-step-shadow': shadows[0],
    '--vf-summary-action-bg': skin.tokens['surface.elevated'], '--vf-summary-action-border': skin.tokens['border.subtle'], '--vf-summary-action-shadow': shadows[0],
    '--vf-route-entry': skin.lockedTokens['route.entry'], '--vf-route-exit': skin.lockedTokens['route.exit'],
    '--vf-broadcast-urgent': skin.lockedTokens['status.urgent'], '--vf-broadcast-warning': skin.lockedTokens['status.warning'],
  })
  const logo = skin.assets.logo?.url || ''
  const hero = skin.assets.hero?.url || ''
  const pageBackground = skin.assets.pageBackground?.url || ''
  if (logo) cssVariables['--vf-skin-logo'] = `url(${JSON.stringify(logo)})`
  if (pageBackground) {
    cssVariables['--vf-page-layer'] = `linear-gradient(180deg, ${skin.tokens['hero.overlayStart']}, ${skin.tokens['hero.overlayEnd']}), url(${JSON.stringify(pageBackground)}) center / cover no-repeat`
  }
  return {
    skinId: skin.resolvedSkinId,
    elevationPreset: skin.tokens['elevation.preset'],
    cssVariables,
    canvas: {
      page: skin.tokens['surface.page'], surface: skin.tokens['surface.card'], elevated: skin.tokens['surface.elevated'],
      foreground: skin.tokens['text.primary'], secondary: skin.tokens['text.secondary'], accent: skin.tokens['brand.secondary'],
      border: skin.tokens['border.subtle'], routeEntry: skin.lockedTokens['route.entry'], routeExit: skin.lockedTokens['route.exit'],
      qrForeground: skin.lockedTokens['qr.foreground'], qrBackground: skin.lockedTokens['qr.background'],
      scrim: skin.tokens['surface.scrim'], inverse: skin.tokens['text.inverse'],
    },
    broadcast: { surface: skin.tokens['surface.elevated'], urgent: skin.lockedTokens['status.urgent'], warning: skin.lockedTokens['status.warning'] },
    annotation: { medical: skin.lockedTokens['annotation.medical'], accessible: skin.lockedTokens['annotation.accessible'], service: skin.lockedTokens['annotation.service'] },
    vr: { toolbar: skin.tokens['surface.scrim'], toolbarText: skin.tokens['text.inverse'] },
    assets: { logo, hero, pageBackground },
  }
}

export function applyResolvedTheme(theme: ResolvedTheme, root: HTMLElement = document.documentElement): void {
  const previous = appliedVariables.get(root) || new Set<string>()
  for (const name of previous) if (!(name in theme.cssVariables)) root.style.removeProperty(name)
  for (const [name, value] of Object.entries(theme.cssVariables)) root.style.setProperty(name, value)
  appliedVariables.set(root, new Set(Object.keys(theme.cssVariables)))
  root.dataset.vfSkin = theme.skinId
  root.dataset.vfHasLogo = theme.assets.logo ? 'true' : 'false'
  root.style.colorScheme = isDarkColor(theme.canvas.page) ? 'dark' : 'light'
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', theme.canvas.page)
  activeTheme = theme
  window.dispatchEvent(new CustomEvent('vf-resolved-theme'))
}

export function getActiveTheme(): ResolvedTheme {
  if (!activeTheme) throw new Error('皮肤尚未初始化')
  return activeTheme
}

function isDarkColor(value: string): boolean {
  if (!/^#[a-f0-9]{6}$/i.test(value)) return true
  const channels = [1, 3, 5].map((start) => Number.parseInt(value.slice(start, start + 2), 16) / 255)
    .map((item) => item <= .04045 ? item / 12.92 : ((item + .055) / 1.055) ** 2.4)
  return .2126 * channels[0]! + .7152 * channels[1]! + .0722 * channels[2]! < .45
}
